import React, { useState, Suspense, useEffect, useMemo } from "react";
import { AnimatePresence, useCycle } from "framer-motion";
import PropTypes from "prop-types";

import StorylineItems from "./StorylineItems";

import SidePanel from "../SidePanel/SidePanel";

const App = ({ cards, containerId }) => {
	const [data, setData] = useState(cards);
	const [selectedItem, setSelectedItem] = useState("");
	const [open, cycleOpen] = useCycle(false, true);

	const findItemIndex = (item) => {
		const index = data.findIndex((i) => i.id === item.id);
		return index;
	};

	const selectItem = (item) => {
		if (!item) {
			setSelectedItem("");
			return;
		}

		const index = findItemIndex(item);
		if (index < 0) return;

		setSelectedItem({ ...data[index], index });
	};

	useEffect(() => {
		const handleEscape = (e) => {
			if (e.key === "Escape") {
				toggleSidePanel();
			}
		};
		document.addEventListener("keydown", handleEscape);

		return () => {
			document.removeEventListener("keydown", handleEscape);
		};
	}, []);

	useEffect(() => {
		if (open === true) {
			document.body.classList.add("panel-open");
		} else {
			document.body.classList.remove("panel-open");
		}
	}, [open]);

	// // useEffect responsible for displaying the sidepanel from a sharable url
	useEffect(() => {
		if (data.length > 0) {
			if (window.location.hash) {
				// if there is a hash in the url we first extract the section Id that it is related to
				let trimmedIncomingContainerId = window.location.search.replace("?", "");
				let parsedContainerId = parseInt(trimmedIncomingContainerId);

				//then we extract the slug of the storyline item that is shared
				let trimmedIncomingStoryline = window.location.hash.replace("#", "");
				if (parsedContainerId == containerId) {
					// find the storyline we want from the storyline array using the trimmed hash
					let selectedStoryline = data.find((storyline) => storyline.slug == trimmedIncomingStoryline);

					// we open the sidePanel related to that specific storyline item that is matches the specific section id
					if (selectedStoryline) {
						toggleSidePanel(selectedStoryline);
					}
				}
			}
		}
	}, []);

	const toggleSidePanel = (item) => {
		if (item) {
			cycleOpen(true);
			selectItem(item);
		} else {
			cycleOpen(false);
			selectItem(false);
			window.history.pushState({}, "", window.location.pathname);
		}
	};

	const updateUrl = (item) => {
		let urlParams = "";
		if (item) {
			// we use the hash param in window.location to store the performer slug
			urlParams = "?" + item.containerId + "#" + item.slug;
		}
		window.history.pushState(null, "", urlParams);
	};

	const handleNext = (item) => {
		const currentIndex = findItemIndex(item);
		const nextIndex = currentIndex + 1;
		const nextItem = data[nextIndex];
		if (nextItem) {
			selectItem(nextItem);
		}
	};

	const handlePrevious = (item) => {
		const currentIndex = findItemIndex(item);
		const prevIndex = currentIndex - 1;
		const prevItem = data[prevIndex];
		if (prevItem) {
			selectItem(prevItem);
		}
	};

	const handleSelectSlide = (index) => {
		if (index < 0 || !data[index]) return;

		selectItem(data[index]);
	};

	const isNextDisabled = () => {
		const currentIndex = findItemIndex(selectedItem);
		const dataLastIndex = data.length - 1;

		if (currentIndex >= dataLastIndex) return true;
		return false;
	};
	const isPreviousDisabled = () => {
		const currentIndex = findItemIndex(selectedItem);

		if (currentIndex <= 0) return true;
		return false;
	};

	const selectedSlide = (index) => {
		return selectedItem.title === data[index].title;
	};

	const handleClickItem = (item) => {
		toggleSidePanel(item);
		updateUrl(item);
	};

	return (
		<>
			<StorylineItems handleClick={handleClickItem} items={data} />
			<AnimatePresence>
				{open && selectedItem && (
					<SidePanel
						numOfSlides={data.length}
						handleSelectSlide={handleSelectSlide}
						selectedSlide={selectedSlide}
						handleNext={() => handleNext(selectedItem)}
						handlePrevious={() => handlePrevious(selectedItem)}
						selectedContent={selectedItem}
						toggleSidePanel={toggleSidePanel}
						isNextDisabled={isNextDisabled()}
						isPreviousDisabled={isPreviousDisabled()}
					/>
				)}
			</AnimatePresence>
		</>
	);
};

export default App;

App.propTypes = {
	json: PropTypes.any,
};
